import React, { useEffect, useMemo, useContext, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  arrayOf,
  customType,
  extend,
  params,
  shape as shapeType,
  string as stringType,
  number as numberType,
  bool,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { getNvalue, getNewKeyword } from '../../utils/ProPlanningToolsHelper.js';

/**
 * Custom equality check for IntentResolver to prevent unnecessary re-renders
 */
function isIntentResolverEqual(prevProps, nextProps) {
  return (
    prevProps.intent === nextProps.intent &&
    prevProps.onResolved === nextProps.onResolved &&
    JSON.stringify(prevProps.additionalSearchParams) === JSON.stringify(nextProps.additionalSearchParams)
  );
}

/**
 * Handle browse redirect (/b/) - extract N-value
 */
const handleBrowseRedirect = (searchRedirect, setNValue, onResolved, intent) => {
  const extractedNValue = getNvalue(searchRedirect);
  if (extractedNValue) {
    setNValue(extractedNValue);
  } else {
    onResolved(intent, null);
  }
};

/**
 * Handle search redirect (/s/) - extract new keyword
 */
const handleSearchRedirect = (searchRedirect, setRedirectedKeyword, onResolved, intent) => {
  const newKeyword = getNewKeyword(searchRedirect);
  if (newKeyword) {
    setRedirectedKeyword(newKeyword);
  } else {
    onResolved(intent, null);
  }
};

/**
 * Process search redirect based on redirect type
 */
const processSearchRedirect = (searchRedirect, setNValue, setRedirectedKeyword, onResolved, intent) => {
  if (searchRedirect.startsWith('/b/')) {
    handleBrowseRedirect(searchRedirect, setNValue, onResolved, intent);
  } else if (searchRedirect.startsWith('/s/')) {
    handleSearchRedirect(searchRedirect, setRedirectedKeyword, onResolved, intent);
  } else {
    onResolved(intent, null);
  }
};

/**
 * Individual resolver component - each instance has its own useLazyDataModel hook
 * This allows multiple parallel queries without cancellation
 */
export const IntentResolver = React.memo(({
  intent,
  onResolved,
  additionalSearchParams
}) => {
  const { storeId, membershipInformation } = useStore();
  const { channel } = useContext(ExperienceContext);

  // Track redirect state for re-querying
  const [nValue, setNValue] = useState(null);
  const [redirectedKeyword, setRedirectedKeyword] = useState(null);

  const loyaltyMembershipInput = useMemo(() => {
    return membershipInformation?.data?.loyaltyMembership || null;
  }, [membershipInformation?.data?.loyaltyMembership]);

  const [findProducts, response] = useLazyDataModel('searchModel', {
    variables: {
      storeId,
      startIndex: 0,
      pageSize: 1,
      channel: channel?.toUpperCase() || 'DESKTOP',
      orderBy: { field: 'BEST_MATCH', order: 'ASC' },
      loyaltyMembershipInput,
      additionalSearchParams,
      isBrandPage: false
    }
  });


  const { data, error } = response || {};

  const processSearchResponse = useCallback((searchData) => {
    const products = searchData?.searchModel?.products;
    const { metadata } = searchData?.searchModel || {};
    const { searchRedirect } = metadata || {};

    if (products && products.length > 0) {
      const [product] = products;
      const { itemId, bundleFlag, bundleItems } = product || {};
      if (bundleFlag) {
        const itemIds = (bundleItems || []).map((bundleItem) => bundleItem.id) || [];
        onResolved(intent, { itemIds });
      } else {
        onResolved(intent, { itemIds: [itemId] });
      }
      return;
    }

    if (searchRedirect) {
      processSearchRedirect(searchRedirect, setNValue, setRedirectedKeyword, onResolved, intent);
      return;
    }

    if (!searchRedirect) {
      onResolved(intent, null);
    }
  }, [intent, onResolved]);

  useEffect(() => {
    if (data) {
      processSearchResponse(data);
    } else if (error) {
      onResolved(intent, null);
    }
  }, [data, error, intent, onResolved, processSearchResponse]);

  // Re-trigger search with N-value for browse redirects
  useEffect(() => {
    if (nValue) {
      findProducts({
        variables: {
          navParam: nValue,
          additionalSearchParams
        }
      });
    }
  }, [nValue, findProducts, additionalSearchParams]);

  // Re-trigger search with new keyword for search redirects
  useEffect(() => {
    if (redirectedKeyword) {
      findProducts({
        variables: {
          keyword: redirectedKeyword,
          additionalSearchParams
        }
      });
    }
  }, [redirectedKeyword, findProducts, additionalSearchParams]);

  useEffect(() => {
    const resolveIntent = async () => {
      await findProducts({
        variables: {
          keyword: intent,
          additionalSearchParams
        }
      });
    };
    if (intent && intent.length >= 1) {
      resolveIntent();
    } else {
      onResolved(intent, null);
    }
  }, [intent, findProducts, additionalSearchParams, onResolved]);

  // This component renders nothing - it's just for the hook
  return null;
}, isIntentResolverEqual);

IntentResolver.displayName = 'IntentResolver';

IntentResolver.propTypes = {
  intent: PropTypes.string.isRequired,
  onResolved: PropTypes.func.isRequired,
  additionalSearchParams: PropTypes.shape({
    deliveryZip: PropTypes.string,
    sponsored: PropTypes.bool,
    svocID: PropTypes.string
  }).isRequired
};

IntentResolver.defaultProps = {};

// Data model for the searchModel query
IntentResolver.dataModel = extend({
  searchModel: params({
    keyword: stringType(),
    navParam: stringType(),
    storeId: stringType(),
    storefilter: customType('StoreFilter').enum(['ALL', 'IN_STORE', 'ONLINE'], 'ALL'),
    loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
      svocID: stringType(),
      programTiers: arrayOf(
        shapeType({
          tier: stringType(),
          program: stringType()
        })
      )
    }),
    additionalSearchParams: customType('AdditionalParams').shape({
      deliveryZip: stringType(),
      sponsored: bool(),
      svocID: stringType()
    }),
    isBrandPage: bool(),
    channel: customType('Channel').enum(['DESKTOP', 'MOBILE', 'TABLET'], 'DESKTOP')
  }).shape({
    metadata: shapeType({
      searchRedirect: stringType(),
      analytics: shapeType({
        semanticTokens: stringType()
      })
    }),
    products: params({
      startIndex: numberType(),
      pageSize: numberType(),
      orderBy: customType('ProductSort').shape({
        field: customType('ProductSortField').enum([
          'TOP_SELLERS', 'MOST_POPULAR', 'TOP_RATED', 'NEW_ITEMS',
          'BEST_MATCH', 'PRODUCT_NAME', 'BRAND_NAME', 'PRICE'
        ], 'BEST_MATCH'),
        order: customType('SortOrder').enum(['ASC', 'DESC'], 'ASC')
      })
    }).arrayOf(shapeType({
      itemId: stringType(),
      bundleFlag: bool(),
      bundleItems: arrayOf(shapeType({
        id: stringType()
      }))
    }))
  })
});
